"use client";

import { useEffect } from "react";
import posthog from "posthog-js";

const reportedOperations = new Set<string>();

/**
 * Reports the first time each guard path fires on a given path (the module
 * survives SPA navigations, so dedup is keyed by operation + pathname) so we
 * can confirm in production that translate/extension DOM mutations are the
 * real cause of the onboarding crashes, and measure how often they happen.
 */
function reportGuardTriggered(operation: "removeChild" | "insertBefore") {
  const dedupKey = `${operation}:${window.location.pathname}`;
  if (reportedOperations.has(dedupKey)) return;
  reportedOperations.add(dedupKey);

  const html = document.documentElement;
  const payload = {
    operation,
    document_lang: html.lang,
    page_translated:
      html.classList.contains("translated-ltr") ||
      html.classList.contains("translated-rtl"),
    path: window.location.pathname,
  };
  console.warn("[TranslateCrashGuard] DOM mutation mismatch guarded", payload);

  if (posthog.__loaded) {
    posthog.capture("dom_mutation_guard_triggered", payload);
  }
}

/**
 * Guards against the well-known React crash caused by browser tools that mutate
 * the DOM out from under React — most commonly Google Translate (very likely on
 * a Spanish site if `lang` is wrong), in-app browser WebViews, and some
 * extensions. They replace/wrap text nodes, so when React later runs
 * `removeChild`/`insertBefore` during reconciliation the target node is no
 * longer where React expects, throwing
 * `NotFoundError: Failed to execute 'removeChild' on 'Node'` and tripping the
 * error boundary ("Algo salió mal").
 *
 * We make both operations no-throw: if the node isn't actually a child of the
 * expected parent, fall back to removing/inserting via its real parent instead
 * of letting the exception bubble up and kill the render tree.
 *
 * Reference: https://github.com/facebook/react/issues/11538
 */
export function TranslateCrashGuard() {
  useEffect(() => {
    if (typeof Node !== "function" || !Node.prototype) return;

    const w = window as typeof window & { __translateCrashGuard?: boolean };
    if (w.__translateCrashGuard) return;
    w.__translateCrashGuard = true;

    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function <T extends Node>(
      this: Node,
      child: T,
    ): T {
      if (child.parentNode !== this) {
        reportGuardTriggered("removeChild");
        if (child.parentNode) {
          child.parentNode.removeChild(child);
        }
        return child;
      }
      return originalRemoveChild.call(this, child) as T;
    } as typeof Node.prototype.removeChild;

    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function <T extends Node>(
      this: Node,
      newNode: T,
      referenceNode: Node | null,
    ): T {
      if (referenceNode && referenceNode.parentNode !== this) {
        reportGuardTriggered("insertBefore");
        // The reference node was moved by translation; append to the intended
        // parent so the new content still renders instead of silently
        // disappearing (position may be slightly off, which is acceptable).
        this.appendChild(newNode);
        return newNode;
      }
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    } as typeof Node.prototype.insertBefore;
  }, []);

  return null;
}
